import React, { useId, useMemo, useState } from "react";
import { t } from "@/i18n";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useValidation } from "@/hooks/useValidation";

const getInitialValues = (fields) =>
  fields.reduce((values, field) => {
    if (field.type === "checkbox") {
      values[field.name] = field.defaultValue ?? false;
    } else {
      values[field.name] =
        field.defaultValue === undefined ? "" : String(field.defaultValue);
    }

    return values;
  }, {});

const CollectUserInput = ({ definition, onSubmit, onCancel }) => {
  const formId = useId();
  const [values, setValues] = useState(() =>
    getInitialValues(definition.fields),
  );

  const validators = useMemo(
    () =>
      definition.fields.reduce((result, field) => {
        result[field.name] = (assert) => {
          const value = values[field.name];

          if (field.type === "checkbox") {
            if (field.required) {
              assert(value === true, "This field must be checked");
            }
            return;
          }

          const hasValue = String(value).trim().length > 0;
          if (field.required) {
            assert(hasValue, "This field is required");
          }

          if (!hasValue) return;

          if (field.type === "radio") {
            assert(
              field.options.some((option) => option.value === value),
              "Select a valid option",
            );
            return;
          }

          if (field.type === "text") {
            if (field.minLength !== undefined) {
              assert(
                value.length >= field.minLength,
                `Must be at least ${field.minLength} characters`,
              );
            }
            if (field.maxLength !== undefined) {
              assert(
                value.length <= field.maxLength,
                `Must be at most ${field.maxLength} characters`,
              );
            }
            return;
          }

          const number = Number(value);
          assert(Number.isFinite(number), "Enter a valid number");
          if (!Number.isFinite(number)) return;

          if (field.min !== undefined) {
            assert(number >= field.min, `Must be at least ${field.min}`);
          }
          if (field.max !== undefined) {
            assert(number <= field.max, `Must be at most ${field.max}`);
          }
        };

        return result;
      }, {}),
    [definition.fields, values],
  );

  const { validate, validationMessages } = useValidation(validators);

  const setValue = (name, value) => {
    setValues((currentValues) => ({ ...currentValues, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    const submittedValues = definition.fields.reduce((result, field) => {
      const value = values[field.name];

      if (field.type === "number") {
        if (String(value).trim()) {
          result[field.name] = Number(value);
        }
      } else {
        result[field.name] = value;
      }

      return result;
    }, {});

    onSubmit(submittedValues);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="gap-sm flex min-w-[80%] flex-col rounded-lg bg-slate-100 p-4"
    >
      <div>
        <h3 className="font-semibold">{definition.title}</h3>
        {definition.description && (
          <p className="text-muted-foreground mt-1">{definition.description}</p>
        )}
      </div>

      {definition.fields.map((field, index) => {
        const id = `${formId}-${field.name}`;

        if (field.type === "checkbox") {
          return (
            <div key={field.name} className="space-y-1">
              <Checkbox
                id={id}
                label={field.label}
                checked={values[field.name]}
                onCheckedChange={(checked) =>
                  setValue(field.name, checked === true)
                }
                validationMessage={validationMessages[field.name]}
              />
              {field.hint && (
                <p className="text-muted-foreground pl-10 text-sm">
                  {field.hint}
                </p>
              )}
            </div>
          );
        }

        if (field.type === "radio") {
          return (
            <fieldset key={field.name} className="space-y-1">
              <legend className="font-semibold">{field.label}</legend>
              {field.hint && (
                <p className="text-muted-foreground text-sm">{field.hint}</p>
              )}
              <div className="space-y-2">
                {field.options.map((option, optionIndex) => {
                  const optionId = `${id}-${optionIndex}`;

                  return (
                    <label
                      key={option.value}
                      htmlFor={optionId}
                      className="flex cursor-pointer items-center gap-2"
                    >
                      <input
                        id={optionId}
                        type="radio"
                        name={id}
                        value={option.value}
                        checked={values[field.name] === option.value}
                        onChange={(event) =>
                          setValue(field.name, event.target.value)
                        }
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>
              {validationMessages[field.name] && (
                <p className="text-destructive text-sm">
                  {validationMessages[field.name]}
                </p>
              )}
            </fieldset>
          );
        }

        return (
          <Input
            key={field.name}
            autoFocus={index === 0}
            id={id}
            type={field.type}
            label={field.label}
            hint={field.hint}
            placeholder={field.placeholder}
            value={values[field.name]}
            min={field.type === "number" ? field.min : undefined}
            max={field.type === "number" ? field.max : undefined}
            step={field.type === "number" ? "any" : undefined}
            maxLength={field.type === "text" ? field.maxLength : undefined}
            onChange={(event) => setValue(field.name, event.target.value)}
            validationMessage={validationMessages[field.name]}
          />
        );
      })}

      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("collections.replace_existing_document.modal.footer.btn.decline")}
        </Button>
        <Button type="submit">{definition.submitLabel ?? "Submit"}</Button>
      </div>
    </form>
  );
};

const CollectedUserInput = ({ definition, result }) => {
  if (result.status === "cancelled") {
    return (
      <div className="bg-muted text-muted-foreground min-w-[80%] rounded-lg border p-4">
        {definition.title} was skipped.
      </div>
    );
  }

  return (
    <div className="bg-background min-w-[80%] rounded-lg border p-4">
      <h3 className="font-semibold">{definition.title}</h3>
      <dl className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2">
        {definition.fields.map((field) => (
          <React.Fragment key={field.name}>
            <dt className="text-muted-foreground">{field.label}</dt>
            <dd className="max-w-64 text-right break-words">
              {field.type === "checkbox"
                ? result.values[field.name]
                  ? "Yes"
                  : "No"
                : result.values[field.name] ?? "Not provided"}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

export { CollectedUserInput, CollectUserInput };
